import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import {
  Wallet, Package, Truck, CheckCircle2, RotateCcw, Heart, Tag, MapPin,
  CreditCard, HelpCircle, Info, Settings, ChevronRight, LogOut,
} from "lucide-react";
import { matchTab } from "./StoreOrders";

const ORDER_STATUS = [
  { icon: Wallet, label: "Belum Bayar", status: "belum_bayar" },
  { icon: Package, label: "Diproses", status: "diproses" },
  { icon: Truck, label: "Dikirim", status: "dikirim" },
  { icon: CheckCircle2, label: "Selesai", status: "selesai" },
  { icon: RotateCcw, label: "Retur", status: "retur" },
];

export default function StoreAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
      api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-blush mx-auto flex items-center justify-center mb-4">
          <Heart className="w-7 h-7 text-brand" />
        </div>
        <div className="font-serif-fashion text-2xl mb-2 text-cocoa">Masuk Akunmu</div>
        <div className="text-sm text-cocoa-muted mb-5">Login untuk melihat pesanan, poin, dan voucher.</div>
        <Link to="/store/auth" data-testid="account-login-btn" className="inline-block px-8 h-11 leading-[44px] rounded-full bg-brand text-white text-sm font-semibold">
          Masuk / Daftar
        </Link>
      </div>
    );
  }

  const menu = [    { icon: Heart, label: "Wishlist", to: "/store/wishlist" },
    { icon: Tag, label: "Voucher Saya", badge: vouchers.length, to: "/store/vouchers" },
    { icon: MapPin, label: "Alamat Saya", to: "/store/addresses" },
    { icon: CreditCard, label: "Metode Pembayaran", to: "/store/help" },
    { icon: HelpCircle, label: "Bantuan & CS", to: "/store/help" },
    { icon: Info, label: "Tentang Kiyana", to: "/store/about" },
  ];

  return (
    <div className="space-y-4 animate-fade-up -mt-4 -mx-4">
      {/* Member card */}
      <div className="bg-gradient-to-br from-[#C4707F] to-[#A85B6A] text-white px-5 pt-6 pb-8 relative">
        <div className="text-center">
          <div className="font-serif-fashion text-2xl tracking-[0.25em]">KIYANA</div>
          <div className="text-[9px] tracking-[0.2em] opacity-80">by Cirebon Cloth</div>
        </div>
        <Settings className="w-5 h-5 absolute top-5 right-5 opacity-80" />
        <div className="flex items-center gap-3 mt-5">
          <div className="w-14 h-14 rounded-full bg-white text-brand flex items-center justify-center font-serif-fashion text-2xl flex-shrink-0">
            {user.name?.[0]}
          </div>
          <div>
            <div className="font-semibold">Hello, {user.name?.split(" ")[0]}!</div>
            <div className="text-[11px] opacity-85">👑 Member {loyalty?.tier || "Silver"} • {loyalty?.points ?? 0} Poin</div>
          </div>
          <ChevronRight className="w-5 h-5 ml-auto opacity-80" />
        </div>
      </div>

      <div className="px-4 space-y-4">
        {/* Pesanan saya */}
        <div className="bg-white rounded-2xl border border-blush-soft p-4 -mt-6 relative z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Pesanan Saya</div>
            <Link to="/store/orders" data-testid="orders-view-all" className="text-[11px] text-brand font-medium">Lihat semua ›</Link>
          </div>
          <div className="grid grid-cols-5">
            {ORDER_STATUS.map((s, i) => {
              const count = orders.filter((o) => matchTab(o, s.status)).length;
              return (
                <button key={i} data-testid={`order-status-${s.label.toLowerCase().replace(" ", "-")}`} onClick={() => nav(`/store/orders?status=${s.status}`)} className="flex flex-col items-center gap-1.5">
                  <div className="relative">
                    <s.icon className="w-5 h-5 text-cocoa" />
                    {count > 0 && (
                      <span data-testid={`status-count-${s.status}`} className="absolute -top-1.5 -right-2.5 bg-brand text-white text-[9px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
                        {count}
                      </span>
                    )}
                  </div>
                  <span className="text-[9px] text-cocoa-muted text-center leading-tight">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Menu list */}
        <div className="bg-white rounded-2xl border border-blush-soft divide-y divide-blush-soft">
          {menu.map((m, i) => (
            <button
              key={i}
              data-testid={`account-menu-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => m.to && nav(m.to)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
            >
              <m.icon className="w-5 h-5 text-cocoa" />
              <span className="flex-1 text-sm">{m.label}</span>
              {m.badge > 0 && <span className="bg-brand text-white text-[10px] rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{m.badge}</span>}
              <ChevronRight className="w-4 h-4 text-cocoa-muted" />
            </button>
          ))}
        </div>

        {/* Riwayat pesanan */}
        <div>
          <h2 className="text-sm font-semibold mb-2">Riwayat Pesanan</h2>
          {orders.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-2xl border border-blush-soft text-xs text-cocoa-muted">Belum ada pesanan.</div>
          ) : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.code}`} onClick={() => nav(`/store/order/${o.id}`)} className="bg-white rounded-2xl p-4 border border-blush-soft cursor-pointer">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <div className="text-[10px] text-cocoa-muted">{formatDateID(o.created_at)}</div>
                      <div className="font-semibold text-sm">#{o.code}</div>
                    </div>
                    <div className="text-[10px] bg-blush text-brand rounded-full px-2 py-0.5 capitalize">{o.payment_status.replace(/_/g, " ")}</div>
                  </div>
                  <div className="text-[11px] text-cocoa-muted mb-2">{o.items.length} produk{o.voucher_code ? ` • Voucher ${o.voucher_code}` : ""}</div>
                  <div className="flex justify-between items-center pt-2 border-t border-blush-soft">
                    <div className="text-[11px] text-cocoa-muted">Total</div>
                    <div className="text-brand font-bold text-sm">{formatIDR(o.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/store"); }}
          className="w-full h-11 rounded-full border border-blush-soft text-sm text-cocoa flex items-center justify-center gap-2 bg-white"
        >
          <LogOut className="w-4 h-4" /> Keluar
        </button>
      </div>
    </div>
  );
}
